import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import axios, { AxiosError, type CancelTokenSource } from 'axios'
import { EnumDropdownItemVariant, type IDropdownItem, type ITabItem, toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import { CountrySelectStore } from 'shared/ui/CountrySelect/store/CountrySelectStore'
import { uiStore } from 'shared/store/uiStore'
import { PhoneApi } from 'entities/Phone'
import {
  ICountriesEnum,
  type IParamsNumberSearch,
  type IResponseNumberSearch,
} from 'entities/Phone/api/types'
import type { IResponseBillingAreaCode, IResponseBillingState } from 'entities/Billing/api/types'
import { ITabsEnum } from 'entities/Phone/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'

const TABS: ITabItem[] = [
  {
    name: 'Local number',
    key: ITabsEnum.LOCAL,
  },
  {
    name: 'Toll-free number',
    key: ITabsEnum.TOLL_FREE,
  },
]

const ERROR_MESSAGE = 'Something went wrong. Please try again later.'
const UK_COUNTRY_CODE = 'GB'

export class ChooseNumberTabsStore {
  private _selectedNumber: IResponseNumberSearch | null = null
  private _numbersMap: Map<string, IResponseNumberSearch> = new Map()
  private _loading = false
  private _loadingNumber = false
  private _state: IResponseBillingState | null = null
  private _areaCode: IResponseBillingAreaCode | null = null
  private _activeTab: ITabsEnum = ITabsEnum.LOCAL
  private _hideTab: ITabsEnum | null = null
  private _filterByDigitsValue = ''
  private _useUnavailableContent = false
  private _searchError: string | null = null

  private _cancelTokenSource: CancelTokenSource | null = null
  private _countryStore: CountrySelectStore
  private _disposeAreaCode: IReactionDisposer | null = null
  private _disposeInit: IReactionDisposer | null = null
  private _disposeState: IReactionDisposer | null = null
  private _disposeCountry: IReactionDisposer | null = null
  private _disposeActiveTab: IReactionDisposer | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._countryStore = new CountrySelectStore({
      isSinchNumberVendor: this._entities.organizationStore.isSinchNumberVendor,
    })
    this.init()
  }

  get countryStore() {
    return this._countryStore
  }

  get selectedNumber(): IResponseNumberSearch | null {
    return this._selectedNumber
  }

  get numbersList(): IResponseNumberSearch[] {
    return Array.from(this._numbersMap.values()).filter((number) =>
      number.number.includes(this._filterByDigitsValue)
    )
  }

  get activeTab(): ITabsEnum {
    return this._activeTab
  }

  get state(): IResponseBillingState | null {
    return this._state
  }

  get areaCode(): IResponseBillingAreaCode | null {
    return this._areaCode
  }

  get loading(): boolean {
    return this._loading
  }

  get loadingNumber(): boolean {
    return this._loadingNumber
  }

  get filterByDigitsValue() {
    return this._filterByDigitsValue
  }

  get tabs() {
    if (this._hideTab) {
      return TABS.filter((tab) => tab.key !== this._hideTab)
    }

    const needReverse = this.numberType === 'toll_free' && this.useUnavailableContent
    return needReverse ? [...TABS].reverse() : TABS
  }

  get hideTab() {
    return this._hideTab
  }

  get useUnavailableContent() {
    return this._useUnavailableContent
  }

  get noResults(): boolean {
    return this.numbersList.length === 0 && !this._loading
  }

  get noAvailableText() {
    if (!this.useUnavailableContent) {
      return null
    }

    if (this.numberType === 'toll_free' && this._activeTab === ITabsEnum.LOCAL) {
      return (
        <>
          Based on your business needs, <br /> local numbers are not available
        </>
      )
    }
    if (this.numberType === 'local' && this._activeTab === ITabsEnum.TOLL_FREE) {
      return (
        <>
          Based on your business needs, <br /> toll-free numbers are not available
        </>
      )
    }

    return null
  }

  get alertDescription() {
    return `${
      this._activeTab === ITabsEnum.LOCAL ? 'Local' : 'Toll-Free'
    } numbers will require verification of your business`
  }

  get textRadioLabel(): string {
    if (this._state) {
      return `${this._state.state_name}, ${this._state.state_code}`
    }

    if (this._activeTab === ITabsEnum.TOLL_FREE) {
      return 'Toll-free'
    }

    if (this.isUKSelected) {
      return 'Mobile'
    }

    return ''
  }

  get statePlaceholder(): string {
    if (this.countryStore.country === ICountriesEnum.CA) return 'Select province'
    return 'Select state'
  }

  get numberType() {
    return this._entities.complianceStore.summary?.numbers_type
  }

  get canSearch(): boolean {
    if (this.isUKSelected) {
      return Boolean(
        this._entities.complianceStore.internationalRegulatoryBundlesStore.isUKApproved
      )
    }

    if (this._activeTab === ITabsEnum.LOCAL) {
      return Boolean(this._areaCode && this.countryStore.country && this._state)
    }

    return Boolean(this.countryStore.country)
  }

  get isUKSelected() {
    return this.countryStore.country === UK_COUNTRY_CODE
  }

  get states(): IDropdownItem[] {
    return this._entities.billingStore.states
      .filter((state) => state.country_code === this.countryStore.country)
      .map((state) => ({
        label: `${state.state_name}, ${state.state_code}`,
        id: state.state_code,
        variant: EnumDropdownItemVariant.CheckedRight,
        customFields: state,
      }))
  }

  get areaCodes(): IDropdownItem[] {
    return this._entities.billingStore.areaCodes
      .filter((areaCode) => areaCode.state_code === this._state?.state_code)
      .map((areaCode) => ({
        label: `${areaCode.state_name} - ${areaCode.area_code}`,
        id: areaCode.area_code,
        variant: EnumDropdownItemVariant.CheckedRight,
        customFields: areaCode,
      }))
  }

  get searchError() {
    return this._searchError
  }

  setNumber = (number: IResponseNumberSearch | null) => {
    this._selectedNumber = number
  }

  setState = (state: IResponseBillingState | null) => {
    this._state = state
    this._areaCode = null
    this._numbersMap.clear()
  }

  setAreaCode = (areaCode: IResponseBillingAreaCode | null) => {
    this._areaCode = areaCode
    this._numbersMap.clear()
  }

  setActiveTab = (activeTab: ITabsEnum) => {
    if (this._activeTab === activeTab) return

    this._filterByDigitsValue = ''
    this._activeTab = activeTab
    this.resetSearchError()
  }

  setFilterByDigitsValue = (value: string) => {
    this._filterByDigitsValue = value.replace(/\D/g, '')
    this.resetSearchError()
    this.handleLoadNumbers()
  }

  setUseUnavailableContent = (value: boolean) => {
    this._useUnavailableContent = value
  }

  setHideTab = (hideTab: ITabsEnum) => {
    this._hideTab = hideTab
  }

  resetSearchError = () => {
    this._searchError = null
  }

  refresh = async () => {
    if (!this.canSearch) return

    try {
      runInAction(() => {
        this._loading = true
        this._numbersMap.clear()
        this._selectedNumber = null
      })

      this.initCancelTokenSource()

      const response = await PhoneApi.getNumbersSearch(this.searchParams, {
        cancelToken: this._cancelTokenSource?.token,
      })

      this.addNumbers(response.data)
    } catch (e) {
      console.error('Error refreshing numbers:', e)

      if (axios.isCancel(e)) return

      toastStore.add({
        type: 'error',
        title: ERROR_MESSAGE,
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  initActiveTab = () => {
    this._activeTab = this.numberType === 'toll_free' ? ITabsEnum.TOLL_FREE : ITabsEnum.LOCAL
  }

  preselectCountry = () => {
    const country = this._entities.complianceStore.summary?.target_country

    if (country === ICountriesEnum.CA) {
      this.countryStore.setCountry(ICountriesEnum.CA)
    }

    if (country === ICountriesEnum.US) {
      this.countryStore.setCountry(ICountriesEnum.US)
    }
  }

  editAnswers = () => {
    uiStore.changeRouteViaVue({
      path: '/settings/organization/compliance/questionnaire',
      name: 'settings.organization.compliance.questionnaire',
      options: {
        state: 'started',
      },
    })
  }

  clear = () => {
    this.setNumber(null)
    this._numbersMap.clear()
    this.setState(null)
    this.setAreaCode(null)
    this.setFilterByDigitsValue('')
    this.countryStore.setCountry(ICountriesEnum.US)
  }

  reset = () => {
    this.resetCancelTokenSource()
    this.disposeReactions()

    this._loading = false
    this._loadingNumber = false

    this.clear()
    this.load()
  }

  resetInboxNumberSelection = () => {
    this.initActiveTab()
    this.clear()

    this.load()
  }

  private init = () => {
    this.setupReactions()
    this.load()
  }

  private setupReactions = () => {
    this.disposeReactions()

    this.reactionCountry()
    this.reactionState()
    this.reactionAreaCode()
    this.reactionActiveTab()
    this.reactionInit()
  }

  private disposeReactions = () => {
    this._disposeAreaCode?.()
    this._disposeInit?.()
    this._disposeState?.()
    this._disposeCountry?.()
    this._disposeActiveTab?.()
  }

  private load = async () => {
    if (!this.canSearch) return

    let isCancelled = false

    try {
      this.initCancelTokenSource()

      runInAction(() => {
        this._loading = true
      })

      const response = await PhoneApi.getNumbersSearch(this.searchParams, {
        cancelToken: this._cancelTokenSource?.token,
      })

      this.addNumbers(response.data)
    } catch (e) {
      logger.error(e)

      if (axios.isCancel(e)) {
        isCancelled = true
        return
      }

      if (e instanceof AxiosError) {
        this._searchError = e.errors.getError('search')

        return
      }

      toastStore.add({
        type: 'error',
        title: ERROR_MESSAGE,
      })
    } finally {
      runInAction(() => {
        if (!isCancelled) {
          this._loading = false
        }
      })
    }
  }

  private addNumbers = (numbers: IResponseNumberSearch[]) => {
    numbers.forEach((item) => {
      this._numbersMap.set(item.number, item)
    })
  }

  private initCancelTokenSource = () => {
    this.resetCancelTokenSource()
    this._cancelTokenSource = axios.CancelToken.source()
  }

  private resetCancelTokenSource = () => {
    if (this._cancelTokenSource) {
      this._cancelTokenSource.cancel('Request cancelled')
      this._cancelTokenSource = null
    }
  }

  private get searchParams(): IParamsNumberSearch {
    return {
      country: this.countryStore.country,
      state: this._state?.state_code || '',
      search: this._areaCode?.area_code || this._filterByDigitsValue,
      toll_free: this._activeTab === ITabsEnum.LOCAL ? '0' : '1',
      mobile: this.isUKSelected || null,
    }
  }

  handleLoadNumbers = () => {
    this._selectedNumber = null
    this._numbersMap.clear()
    this.load()
  }

  reactionActiveTab = () => {
    this._disposeActiveTab?.()
    this._disposeActiveTab = reaction(
      () => this._activeTab,
      () => {
        this._state = null
        this._areaCode = null
        this.handleLoadNumbers()
      }
    )
  }

  reactionCountry = () => {
    this._disposeCountry?.()
    this._disposeCountry = reaction(
      () => this.countryStore.country,
      (country) => {
        this._state = null
        this._areaCode = null
        this.handleLoadNumbers()
        if (country === ICountriesEnum.GB) this._activeTab = ITabsEnum.LOCAL
      }
    )
  }

  reactionState = () => {
    this._disposeState?.()
    this._disposeState = reaction(
      () => this._state,
      () => {
        this._areaCode = null
        this._selectedNumber = null
        this._numbersMap.clear()
      }
    )
  }

  reactionInit = () => {
    this._disposeInit?.()
    this._disposeInit = reaction(
      () => [
        this._entities.subscriptionStore.isAgencyWithoutSubscription,
        this._entities.usersStore.user?.isViewOnlyRole,
        this._entities.usersStore.user?.isRequireNumber,
      ],
      () => this.handleLoadNumbers()
    )
  }

  reactionAreaCode = () => {
    this._disposeAreaCode?.()
    this._disposeAreaCode = reaction(
      () => this._areaCode,
      () => this.handleLoadNumbers()
    )
  }
}
